import { AnimatePresence, motion, type Transition } from "motion/react";
import React from "react";

/**
 * A height-collapsing reveal. When `show` is true the children occupy their
 * natural height; when false they collapse to zero height (and fade) and then
 * unmount. This is the single home for the step walkthrough's core collapse
 * mechanism — "the heading/description folds its own height away so the field
 * below rises smoothly into its place."
 *
 * `AnimatePresence` plays the exit (height → 0) before unmount; the
 * `overflow-hidden` box clips the content as the height animates. Pure
 * mechanism — no copy or spacing of its own; the caller owns what's inside
 * (including any bottom spacing, which collapses along with the block).
 */
export function CollapsingBlock({
    show,
    transition,
    className,
    children
}: {
    show: boolean;
    /** Timing for the height/opacity animation (e.g. the surface-collapse feel). */
    transition?: Transition;
    /** Extra classes on the collapsing box (already `overflow-hidden`). */
    className?: string;
    children: React.ReactNode;
}): React.ReactElement {
    return (
        <AnimatePresence initial={false}>
            {show && (
                <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={transition}
                    className={
                        className
                            ? `overflow-hidden ${className}`
                            : "overflow-hidden"
                    }
                >
                    {children}
                </motion.div>
            )}
        </AnimatePresence>
    );
}
